@if ({
  hasData: paginationSelectors.hasData | select,
  awaitingFirstLoad: defaultSelectorContainer.awaitingFirstLoad | select,
  awaitingAnyLoad: defaultSelectorContainer.awaitingAnyLoad | select:false,
  rowCount: rowCountSelector | select:0
}; as vm) {
  <rn-table-height-adjuster>
    <table mat-table [dataSource]="dataSource" [attr.aria-label]="tableName"
           matSort (matSortChange)="onMatSortChange($event)"
           [multiTemplateDataRows]="useExpansionComponents"
           [ngClass]="{ 'rn-table-no-pagination': !showPagination }">
      <tr mat-header-row *matHeaderRowDef="vm.awaitingAnyLoad || vm.hasData ? columnOrder : columnOrderForNoData"></tr>
      <tr mat-row *matRowDef="let row; columns: columnOrder" [ngClass]="generateRowCssClassesFor(row)" class="rn-table-row"></tr>

      @if (useHiddenDataIndicatorRow) {
        <tr mat-footer-row *matFooterRowDef="hiddenDataIndicatorColumnOrder | select:[]" class="bg-background"
            [class.h-0]="!(showHiddenDataIndicatorRow | select:false)" rnHiddenUntilLoaded [reveal]="!vm.awaitingFirstLoad">
      }

      @if (useExpansionComponents) {
        <tr mat-row class="rn-expansion-row" *matRowDef="let row; columns: ['expanded']"
            [ngClass]="{ 'rn-expansion-row-expanded': rowExpansionService.isExpanded(row) }"></tr>
      }

      <ng-container matColumnDef="expanded">
        <td *matCellDef="let row" mat-cell [attr.colspan]="numberOfDataColumns">
          <div class="rn-expansion-row-content" [@expansionRowContentTransition]="rowExpansionService.isExpanded(row) ? 'expanded' : 'collapsed'">
            <ng-template #rowComponentContainer rvDynamicComponentHost [data]="{
                row: row,
                componentClass: expansionRowComponent.componentClass,
                componentProperties: expansionRowComponent.componentProperties ?? {}
              }"/>
          </div>
        </td>
      </ng-container>

      @if (useStandardNoDataMessageRow) {
        <!--
          We have to use mat-* CSS classes with *matNoDataRow instead of the usual mat-* directives we use everywhere else, because... reasons?
          See https://stackoverflow.com/questions/62698551/angular-material-matnodatarow-doesnt-work#comment112097235_62706478
        -->
        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell border-b-0" [colSpan]="columnOrderForNoData.length">
            <p rnHiddenUntilLoaded [reveal]="!vm.awaitingFirstLoad">
              {{ noDataRowMessage | select }}
            </p>
          </td>
        </tr>
      }

      <ng-container matColumnDef="_expansionRowToggle">
        <th mat-header-cell *matHeaderCellDef scope="col" class="rn-header-cell-for-icon-button"></th>

        <td mat-cell *matCellDef="let row" class="rn-row-expansion-toggle-cell">
          <div class="rn-cell-content">
            @if (showExpansionControlFor(row)) {
              <rn-expansion-toggle [rowObject]="row"/>
            }
          </div>
        </td>
      </ng-container>

      <ng-container matColumnDef="_rowSelectionCheckboxes">
        <th mat-header-cell *matHeaderCellDef scope="col" class="rn-header-cell-for-icon-button">
          <div class="rn-cell-content">
            <rn-all-rows-selection-checkbox [data]="rowSelectionDataSourceSelector | select:[]" [rowSelectionChangeAction]="rowSelectionChangeAction"/>
          </div>
        </th>

        <td mat-cell *matCellDef="let row">
          <div class="rn-cell-content">
            <rn-row-selection-checkbox [rowObject]="row" [enabled]="rowSelectionEnabledFor(row)" [tooltips]="rowSelectionTooltipsFor(row)"/>
          </div>
        </td>
      </ng-container>

      <!-- eslint-disable-next-line @angular-eslint/template/prefer-contextual-for-variables -- Scoping nested for loop indices. -->
      @for (column of columns; track column.columnName; let columnIndex = $index) {
        <ng-container [matColumnDef]="column.columnName">
          <th mat-header-cell *matHeaderCellDef [mat-sort-header]="column.sortKey" [disabled]="!column.sortable || !vm.rowCount"
              class="{{ column.headerCssClasses }}">
            <div class="rn-cell-content" [matTooltip]="column.headerToolTip">
              {{ column.header }}
            </div>
          </th>

          <td mat-cell *matCellDef="let row" [ngClass]="generateCellCssClassesFor(row, column.cssClasses)">
            <div class="rn-cell-content">
              @if (column.isComponent) {
                <ng-template #cellComponentContainer rvDynamicComponentHost [data]="{ row: row, column: column }"/>
              } @else if (column.isChip) {
                <mat-chip-set>
                  @if (column.isStatusChip) {
                    @if (column.valueSelectorCreator) {
                      <rn-status-chip [status]="column.valueSelectorCreator(row) | select" [colors]="column.statusChipColors"
                                      [displayableStatusNames]="column.displayableStatusNames"/>
                    }
                  } @else {
                    @if (column.valueSelectorCreator) {
                      <mat-chip [color]="column.chipColorFor(row)">
                        {{ column.valueSelectorCreator(row) | select | format:column.format }}
                      </mat-chip>
                    }
                  }
                </mat-chip-set>
              } @else if (column.isLink) {
                @if (column.isSimpleLink) {
                  <a (click)="column.clickHandler(row)">
                    <ng-container *ngTemplateOutlet="cellDataOnly"/>
                  </a>
                } @else {
                  <a [rvNamedRouterLink]="column.routeName" [rvNamedRouterLinkParams]="buildLinkParamsFor(column, row)">
                    <ng-container *ngTemplateOutlet="cellDataOnly"/>
                  </a>
                }
              } @else {
                <ng-container *ngTemplateOutlet="cellDataOnly"/>
              }

              <ng-template #cellDataOnly>
                @if (column.truncate) {
                  @if (column.valueSelectorCreator) {
                    <span rnTruncate>
                      {{ column.valueSelectorCreator(row) | select | format:column.format }}
                    </span>
                  }
                } @else {
                  @if (column.valueSelectorCreator) {
                    <span>
                      {{ column.valueSelectorCreator(row) | select | format:column.format }}
                    </span>
                  }
                }
              </ng-template>
            </div>

            @if (useExpansionCells) {
              <div class="rn-expansion-content" [@expansionRowContentTransition]="rowExpansionService.isExpanded(row) ? 'expanded' : 'collapsed'">
                @for (expansionCellDefinition of expansionCellDefinitionsFor(columnIndex); track $index) {
                  <div class="flex flex-col">
                    @if (expansionCellDefinition.showIf(row)) {
                      <span rnTruncate class="min-h-[1lh] mat-caption expansion-row-title">{{ expansionCellDefinition.headerFor(row) }}</span>

                      @if (expansionCellDefinition.hasTextValue) {
                        <span rnTruncate class="min-h-[1lh]">
                          {{ expansionCellDefinition.valueFor(row) | format:expansionCellDefinition.format }}
                        </span>
                      } @else {
                        <ng-template #expansionCellComponentContainer rvDynamicComponentHost
                                     [data]="{
                                       row: row,
                                       componentClass: expansionCellDefinition.hasComponentValue ? expansionCellDefinition.componentClass: undefined,
                                       componentProperties: expansionCellDefinition.hasComponentValue ? expansionCellDefinition.componentProperties : {}
                                     }"/>
                      }
                    }
                  </div>
                }
              </div>
            }
          </td>

          <td mat-footer-cell *matFooterCellDef [colSpan]="columnOrder.length">
            <div class="flex flex-row flex-center-center gap-16">
              <span>{{ hiddenDataIndicatorRowMessage | select }}</span>

              <button mat-button (click)="hiddenDataIndicatorRowClickHandler()">
                {{ hiddenDataIndicatorRowButtonLabel | select }}
              </button>
            </div>
          </td>
        </ng-container>
      }

      <ng-container matColumnDef="_actions">
        <!-- eslint-disable-next-line @angular-eslint/template/no-inline-styles -- This is a dynamic style that is calculated programmatically. -->
        <th mat-header-cell *matHeaderCellDef [style.width.rem]="actionsColumnWidthInRems | async">Action</th>

        <td mat-cell *matCellDef="let row">
          <div class="flex flex-row justify-end">
            @if (showActionButtons) {
              @for (actionButton of actionButtons; track $index) {
                <div class="rn-cell-content">
                  @if (actionButton.visibleFor(row)) {
                    <button mat-icon-button
                            [matTooltip]="actionButton.tooltipFor(row)"
                            [color]="actionButton.colorFor(row)"
                            [disabled]="!actionButton.enabledFor(row)"
                            (click)="actionButton.clickHandler(row)">
                      @if (actionButton.badgeFor(row)) {
                        <mat-icon [ngClass]="generateCellCssClassesFor(row, actionButton.cssClasses)" [matBadge]="actionButton.badgeFor(row)"
                                  matBadgeSize="small"
                                  class="mat-badge-error top-[0.375rem]">  <!-- Adding a badge moves the icon up for some reason, so nudge it back down. -->
                          {{ actionButton.iconName }}
                        </mat-icon>
                      } @else {
                        @if (actionButton.superscriptFor(row)) {
                          <span [matBadge]="actionButton.superscriptFor(row)" class="mat-badge-transparent">
                            <ng-container *ngTemplateOutlet="buttonIcon"/>
                          </span>
                        } @else {
                          <ng-container *ngTemplateOutlet="buttonIcon"/>
                        }
                      }

                      <ng-template #buttonIcon>
                        <mat-icon [ngClass]="generateCellCssClassesFor(row, actionButton.cssClasses)">{{ actionButton.iconName }}</mat-icon>
                      </ng-template>
                    </button>
                  }
                </div>
              }
            }

            @if (showKebabMenuActions) {
              @if (visibleKebabMenuActionsFor(row); as visibleKebabMenuActions) {
                <div class="rn-cell-content">
                  <button mat-icon-button [matMenuTriggerFor]="rowActionsMenu">
                    <mat-icon class="text-surface-contrast">more_vert</mat-icon>
                  </button>

                  <mat-menu #rowActionsMenu="matMenu">
                    <ng-template matMenuContent>
                      @for (kebabMenuAction of visibleKebabMenuActions; track kebabMenuAction.label) {
                        <button mat-menu-item (click)="kebabMenuAction.clickHandler(row)">
                          <span>{{ kebabMenuAction.label }}</span>
                        </button>
                      }
                    </ng-template>
                  </mat-menu>
                </div>
              } @else {
                <button mat-icon-button [disabled]="true">
                  <!-- No icon.  This button takes up space like a kebab button, but is effectively not there because it's invisible and disabled. -->
                </button>
              }
            }
          </div>
        </td>
      </ng-container>
    </table>
  </rn-table-height-adjuster>

  <!-- Note that the "loaded" class is removed inside PaginationComponent when the user clicks the previous/next page buttons, and eventually restored here. -->
  @if (showPagination && vm.hasData) {
    <rn-pagination [class.loaded]="!vm.awaitingAnyLoad"/>
  }

  @if (useCustomNoDataComponent) {
    <div [hidden]="vm.awaitingFirstLoad || vm.hasData">
      <ng-template #customNoDataComponentContainer rvDynamicComponentHost/>
    </div>
  }
}
